import React, { useEffect, useRef, useState } from 'react';

import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
import { Bar } from 'react-chartjs-2';
import i18n from 'i18n-js';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartOptions,
    Legend,
    LegendElement,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';

import {
    createDateHistoryList,
    getChartDataSet,
    getChartLabels,
    isChartStacked,
} from '../logic';

import styles from '../../../styles';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { DateFilterType, DateHistoryData } from 'store/uiStore/interfaces';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export enum ChartDateFilter {
    HOURS = 0,
    DAYS = 1,
    WEEKS = 2,
    MONTHS = 3,
}

export interface ChartDataItem {
    name: string;
    date: string;
    amount: number;
}

export interface ChartBarCheckbox {
    id: string;
    name: string;
    label: JSX.Element;
}

interface ChartsProps {
    dateFilter: DateFilterType;
    onDateClick: (
        selectedDates: {
            startDate: Date;
            endDate: Date;
        },
        dateFilter: DateFilterType,
        dateHistoryList: DateHistoryData[]
    ) => void;
    selectedDatesHistory: DateHistoryData[];
    checkboxList: ChartBarCheckbox[];
    dataList: ChartDataItem[];
    currency: string;
}


export const Charts = ({
    dateFilter,
    onDateClick,
    selectedDatesHistory,
    checkboxList,
    dataList,
    currency,
}: ChartsProps) => {
    const classes = styles();
    const [selectedServices, setSelectedServices] = useState(checkboxList.map(() => true));
    const [primaryDateFilter, setPrimaryDateFilter] = useState(dateFilter);
    const [secondaryDateFilter, setSecondaryDateFilter] = useState(ChartDateFilter.HOURS);
    const [dataRangeDaysDifference, setDataRangeDaysDifference] = useState(0);
    const [showFakeData] = useState(false);

    const chartRef = useRef<ChartJSOrUndefined<'bar', number[], string>>(null);

    const labels = getChartLabels(primaryDateFilter, secondaryDateFilter, selectedDatesHistory);
    const options: ChartOptions<'bar'> = {
        onClick: (evt: any, elements: any, myChart: any) => {
            const points = myChart.getElementsAtEventForMode(
                evt,
                'nearest',
                { intersect: true },
                true
            );

            if (points.length) {
                const firstPoint = points[0];

                // const label = myChart.data.labels[firstPoint.index];
                // const value = myChart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];

                onXAxisClick(firstPoint.index);
            }
        },
        onHover: (event: any, chartElement: any) => {
            if (chartElement.length === 1) {
                event.native.target.style.cursor = 'pointer';
            }
            if (chartElement.length === 0) {
                event.native.target.style.cursor = 'default';
            }
        },
        plugins: {
            legend: {
                onClick: (_, legendItem, legend) => {
                    const index = legendItem.datasetIndex;
                    switchCheckbox(index);
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: isChartStacked(primaryDateFilter, secondaryDateFilter),
            },
            y: {
                stacked: isChartStacked(primaryDateFilter, secondaryDateFilter),
            },
        },
    };
    const data = {
        labels: labels.map((label) => label.name),
        datasets: getChartDataSet(
            selectedServices,
            checkboxList,
            labels,
            dataList,
            primaryDateFilter,
            secondaryDateFilter,
            showFakeData
        ),
        hover: {
            mode: 'label',
        },
    };

    function hideLegendItem(legend: LegendElement<"bar">, index: number) {
        let ci = legend.chart;
        [ci.getDatasetMeta(index)].forEach(function (meta) {
            meta.hidden = selectedServices[index];
        });
        ci.update();
    }

    function switchCheckbox(index: number) {
        // @ts-ignore
        hideLegendItem(chartRef?.current?.legend, index);
        const newSelectedServices = [...selectedServices];
        newSelectedServices[index] = !selectedServices[index];
        setSelectedServices(newSelectedServices);
    }

    function getCheckboxList() {
        return checkboxList.map((bar, i) => {
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={() => switchCheckbox(i)}
                            checked={selectedServices[i]}
                            color="primary"
                        />
                    }
                    label={bar.label}
                />
            );
        });
    }

    function getSecondaryDateFilters() {
        return (
            <div>
                <Button
                    onClick={() => setSecondaryDateFilter(ChartDateFilter.HOURS)}
                    className={classes.mRight20}
                    color="primary"
                    variant={secondaryDateFilter === ChartDateFilter.HOURS ? 'contained' : 'text'}
                >
                    {i18n.t('common.hours')}
                </Button>
                <Button
                    onClick={() => setSecondaryDateFilter(ChartDateFilter.DAYS)}
                    className={classes.mRight20}
                    color="primary"
                    disabled={primaryDateFilter === 1 || dataRangeDaysDifference <= 1}
                    variant={secondaryDateFilter === ChartDateFilter.DAYS ? 'contained' : 'text'}
                >
                    {i18n.t('common.days')}
                </Button>
                <Button
                    onClick={() => setSecondaryDateFilter(ChartDateFilter.WEEKS)}
                    color="primary"
                    disabled={
                        primaryDateFilter === 1 ||
                        primaryDateFilter === 2 ||
                        dataRangeDaysDifference < 7
                    }
                    variant={secondaryDateFilter === ChartDateFilter.WEEKS ? 'contained' : 'text'}
                >
                    {i18n.t('common.weeks')}
                </Button>
            </div>
        );
    }

    function onXAxisClick(clickedIndex: number) {
        const clickedDate = labels[clickedIndex].date;
        const clickedMonthDay = clickedDate.getDate();

        let endDate = new Date(),
            startDate = clickedDate,
            dateHistoryList: any[] = [],
            newDateFilter = primaryDateFilter - 1;

        const lastHistoryDate = selectedDatesHistory[selectedDatesHistory.length - 1].startDate;

        const lastHistoryYear = lastHistoryDate.getFullYear();
        const lastHistoryMonth = lastHistoryDate.getMonth();
        if (secondaryDateFilter === ChartDateFilter.HOURS) {
            return;
        }
        if (primaryDateFilter === DateFilterType.CUSTOM) {
            switch (secondaryDateFilter) {
                case ChartDateFilter.DAYS:
                    if (selectedDatesHistory.length) {
                        const selectedDayNumber = clickedDate.getDate();

                        startDate = new Date(lastHistoryYear, lastHistoryMonth, selectedDayNumber);
                        endDate = new Date(
                            lastHistoryDate.getFullYear(),
                            lastHistoryDate.getMonth(),
                            selectedDayNumber + 1,
                            20,
                            59
                        );

                        dateHistoryList = [...selectedDatesHistory];
                        dateHistoryList.push({
                            name: `${selectedDayNumber} ${i18n.t(
                                'common.monthNames.' + lastHistoryMonth
                            )}`,
                            startDate,
                            endDate,
                        });
                    }
                    break;
                case ChartDateFilter.WEEKS:
                    const selectedDayNumber = clickedDate.getDate();

                    startDate = new Date(lastHistoryYear, lastHistoryMonth, selectedDayNumber);
                    endDate = new Date(
                        lastHistoryDate.getFullYear(),
                        lastHistoryDate.getMonth(),
                        selectedDayNumber + 1,
                        20,
                        59
                    );

                    dateHistoryList = [...selectedDatesHistory];
                    dateHistoryList.push({
                        name: `${selectedDayNumber} ${i18n.t(
                            'common.monthNames.' + lastHistoryMonth
                        )}`,
                        startDate,
                        endDate,
                    });
                    break;
                case ChartDateFilter.MONTHS:
                    const currentMonth = clickedDate.getMonth();
                    const currentYear = clickedDate.getFullYear();
                    endDate = new Date(currentYear, currentMonth + 1, 1);
                    dateHistoryList = [...selectedDatesHistory];
                    dateHistoryList.push({
                        name: `${i18n.t('common.monthNames.' + currentMonth)} ${currentYear}`,
                        startDate,
                        endDate,
                    });
                    setSecondaryDateFilter(ChartDateFilter.WEEKS);
            }
        } else {
            switch (primaryDateFilter) {
                case DateFilterType.DAY:
                    return;
                case DateFilterType.WEEK:
                    if (selectedDatesHistory.length) {
                        const selectedDayNumber = clickedDate.getDate();

                        startDate = new Date(lastHistoryYear, lastHistoryMonth, selectedDayNumber);
                        endDate = new Date(
                            lastHistoryDate.getFullYear(),
                            lastHistoryDate.getMonth(),
                            selectedDayNumber + 1,
                            20,
                            59
                        );

                        dateHistoryList = [...selectedDatesHistory];
                        dateHistoryList.push({
                            name: `${selectedDayNumber} ${i18n.t(
                                'common.monthNames.' + lastHistoryMonth
                            )}`,
                            startDate,
                            endDate,
                        });
                    }
                    newDateFilter = DateFilterType.DAY;
                    break;
                case DateFilterType.MONTH:
                    if (secondaryDateFilter === ChartDateFilter.DAYS) {
                        const dayNumber = clickedDate.getDate();

                        startDate = new Date(lastHistoryYear, lastHistoryMonth, dayNumber);
                        endDate = new Date(
                            lastHistoryDate.getFullYear(),
                            lastHistoryDate.getMonth(),
                            dayNumber + 1,
                            20,
                            59
                        );

                        dateHistoryList = [...selectedDatesHistory];
                        dateHistoryList.push({
                            name: `${dayNumber} ${i18n.t('common.monthNames.' + lastHistoryMonth)}`,
                            startDate,
                            endDate,
                        });
                        break;
                    } else {
                        let weekCount = clickedIndex + 1;

                        endDate = new Date(lastHistoryYear, lastHistoryMonth, clickedIndex + 1);

                        if (selectedDatesHistory.length) {
                            startDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                clickedDate.getDate()
                            );
                            endDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                clickedDate.getDate() + 7
                            );

                            dateHistoryList = [...selectedDatesHistory];
                            dateHistoryList.push({
                                name: `${weekCount} ${i18n.t('common.week')}`,
                                startDate,
                                endDate,
                            });
                        } else {
                            dateHistoryList = createDateHistoryList(3, clickedDate, weekCount);
                        }
                    }

                    break;
                case DateFilterType.YEAR:
                    switch (secondaryDateFilter) {
                        case ChartDateFilter.MONTHS:
                            let weekCount = clickedIndex + 1;
                            setSecondaryDateFilter(ChartDateFilter.WEEKS);
                            dateHistoryList = [...selectedDatesHistory];
                            startDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                clickedDate.getDate()
                            );
                            endDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                clickedDate.getDate() + 7
                            );
                            dateHistoryList.push({
                                name: `${weekCount} ${i18n.t('common.week')}`,
                                startDate,
                                endDate,
                            });
                            endDate = new Date(
                                clickedDate.getFullYear(),
                                clickedDate.getMonth() + 1,
                                1
                            );
                            dateHistoryList = createDateHistoryList(2, clickedDate);
                            break;
                        case ChartDateFilter.WEEKS:
                            const selectedDayNumber = clickedDate.getDate();
                            weekCount = clickedIndex + 1;
                            startDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                selectedDayNumber
                            );
                            endDate = new Date(
                                lastHistoryYear,
                                lastHistoryMonth,
                                clickedDate.getDate() + 7
                            );
                            dateHistoryList = [...selectedDatesHistory];
                            dateHistoryList.push({
                                name: `${weekCount} ${i18n.t('common.week')}`,
                                startDate,
                                endDate,
                            });
                            break;
                        case ChartDateFilter.DAYS:
                            const dayNumber = clickedDate.getDate();

                            startDate = new Date(lastHistoryYear, lastHistoryMonth, dayNumber);
                            endDate = new Date(
                                lastHistoryDate.getFullYear(),
                                lastHistoryDate.getMonth(),
                                dayNumber + 1,
                                20,
                                59
                            );

                            dateHistoryList = [...selectedDatesHistory];
                            dateHistoryList.push({
                                name: `${dayNumber} ${i18n.t(
                                    'common.monthNames.' + lastHistoryMonth
                                )}`,
                                startDate,
                                endDate,
                            });
                            break;
                    }
            }
        }

        //setPrimaryDateFilter(newDateFilter);
        onDateClick(
            {
                startDate,
                endDate,
            },
            dateFilter,
            dateHistoryList
        );
    }

    useEffect(() => {
        setPrimaryDateFilter(dateFilter);
    }, [dateFilter]);
    useEffect(() => {
        if (!!selectedDatesHistory?.length) {
            const startDate = selectedDatesHistory[selectedDatesHistory.length - 1].startDate;
            const endDate = selectedDatesHistory[selectedDatesHistory.length - 1].endDate;
            if (startDate && endDate) {
                const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                if (diffHours <= 48) {
                    setSecondaryDateFilter(ChartDateFilter.HOURS);
                } else if (
                    (dateFilter === DateFilterType.YEAR && selectedDatesHistory.length <= 1) ||
                    (diffDays > 31 && secondaryDateFilter !== ChartDateFilter.WEEKS)
                ) {
                    setSecondaryDateFilter(ChartDateFilter.MONTHS);
                } else if (
                    (diffDays >= 30 && diffDays <= 31) ||
                    (selectedDatesHistory[0].name.includes('February') &&
                        (diffDays == 28 || diffDays == 29))
                ) {
                    setSecondaryDateFilter(ChartDateFilter.WEEKS);
                } else if (primaryDateFilter !== DateFilterType.CUSTOM) {
                    setSecondaryDateFilter(ChartDateFilter.DAYS);
                }

                setDataRangeDaysDifference(diffDays);
            }

            if (selectedDatesHistory.length === 1) {
                setPrimaryDateFilter(dateFilter);
            }
        }
    }, [selectedDatesHistory]);
    return (
        <>
            <div className={classes.chartContainer}>
                <span className={classes.chartCurrencyText}>{currency}</span>
                <Bar id={'chartContainer'} ref={chartRef} options={options} data={data} />
            </div>
            <div className={classes.chartBottomContainer}>
                <div>{getCheckboxList()}</div>
                {getSecondaryDateFilters()}
            </div>
        </>
    );
};
